import React, { useEffect, useMemo, useState } from 'react';
import SubsectionPanel from '../../common/SubsectionPanel/SubsectionPanel';
import { TimeWritingRestDataProvider } from '../../../data/TimeWritingRestDataProvider';
import { UsersRestDataProvider, User } from '../../../data/UsersRestDataProvider';
import { TimeWritingEntry } from '../../../models/TimeWritingEntry';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import './ProjectTimeWriting.scss';
import { useTranslation } from 'react-i18next';

interface ProjectTimeWritingProps {
    projectId: string;
}

const ProjectTimeWriting: React.FC<ProjectTimeWritingProps> = ({ projectId }) => {
    const { t } = useTranslation(['common', 'screens']);
    const [entries, setEntries] = useState<TimeWritingEntry[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const timeWritingProvider = useMemo(() => new TimeWritingRestDataProvider(), []);
    const usersProvider = useMemo(() => new UsersRestDataProvider(), []);

    useEffect(() => {
        if (!projectId) return;
        setLoading(true);
        setError(null);
        Promise.all([
            timeWritingProvider.getByProject(projectId),
            usersProvider.getAll(),
        ])
            .then(([fetchedEntries, fetchedUsers]) => {
                setEntries(fetchedEntries.slice().sort((a, b) => b.date.localeCompare(a.date)));
                setUsers(fetchedUsers);
            })
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, [projectId]);

    const userMap = useMemo(
        () => Object.fromEntries(users.map(u => [u.id, u.fullName])),
        [users]
    );

    const columns: DetailedListColumn<TimeWritingEntry>[] = useMemo(() => [
        {
            key: 'date',
            header: t('screens:projectTimeWriting.columns.date'),
            width: 160,
            render: e => e.date,
        },
        {
            key: 'userId',
            header: t('screens:projectTimeWriting.columns.user'),
            width: 160,
            render: e => userMap[e.userId] ?? e.userId,
        },
        {
            key: 'hours',
            header: t('screens:projectTimeWriting.columns.hours'),
            width: 90,
            align: 'right',
            render: e => Number(e.hours).toFixed(2),
        },
        {
            key: 'activityDescription',
            header: t('screens:projectTimeWriting.columns.activityDescription'),
        },
    ], [userMap, t]);

    const totalHours = useMemo(
        () => entries.reduce((sum, e) => sum + Number(e.hours), 0),
        [entries]
    );

    if (loading) return <SubsectionPanel title={t('screens:projectTimeWriting.title')}><div>{t('common:loading')}</div></SubsectionPanel>;
    if (error) return <SubsectionPanel title={t('screens:projectTimeWriting.title')}><div style={{ color: 'red' }}>{error}</div></SubsectionPanel>;

    return (
        <SubsectionPanel title={t('screens:projectTimeWriting.title')}>
            {entries.length === 0 ? (
                <p className="text-muted">{t('screens:projectTimeWriting.noEntries')}</p>
            ) : (
                <>
                    <DetailedListComponent<TimeWritingEntry>
                        columns={columns}
                        items={entries}
                        rowKey={e => e.id}
                        editRole="__none__"
                        deleteRole="__none__"
                        addRole="__none__"
                        className="project-tw-list"
                    />
                    <div className="project-tw-list__total">
                        {t('screens:projectTimeWriting.total')} {totalHours.toFixed(2)} hrs
                    </div>
                </>
            )}
        </SubsectionPanel>
    );
};

export default ProjectTimeWriting;

